import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';

function Profile() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        // Fetch user data from Firestore
        try {
          const q = query(
            collection(db, 'users_web'),
            where('uid', '==', currentUser.uid)
          );
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            setUserData(querySnapshot.docs[0].data());
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        navigate('/');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading your profile...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.logo}>Jebena</h1>
        <button onClick={handleSignOut} style={styles.signOutBtn}>
          Sign Out
        </button>
      </div>

      <div style={styles.content}>
        <div style={styles.welcomeCard}>
          <div style={styles.welcomeHeader}>
            <h2 style={styles.welcomeTitle}>Welcome to Jebena</h2>
            <div style={styles.statusBadge}>Active</div>
          </div>
          <p style={styles.welcomeMessage}>
            We will send you information on next steps and offline events here and via email.
          </p>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Email Verification</div>
              <div style={styles.infoValue}>Check your inbox</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Local Events</div>
              <div style={styles.infoValue}>In progress</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Matchmaking</div>
              <div style={styles.infoValue}>In progress</div>
            </div>
          </div>
        </div>

        <div style={styles.profileCard}>
          <h3 style={styles.sectionTitle}>Your Profile</h3>
          
          {userData ? (
            <>
              {userData.photoURL && (
                <div style={styles.profilePictureContainer}>
                  <img 
                    src={userData.photoURL} 
                    alt="Profile" 
                    style={styles.profilePicture}
                  />
                </div>
              )}
              
              <div style={styles.profileInfo}>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Name:</span>
                  <span style={styles.value}>
                    {userData.firstName} {userData.lastName}
                  </span>
                </div>
              
              <div style={styles.infoRow}>
                <span style={styles.label}>Email:</span>
                <span style={styles.value}>{user.email}</span>
              </div>
              
              <div style={styles.infoRow}>
                <span style={styles.label}>Birthday:</span>
                <span style={styles.value}>{userData.birthday}</span>
              </div>
              
              <div style={styles.infoRow}>
                <span style={styles.label}>Gender:</span>
                <span style={styles.value}>{userData.gender}</span>
              </div>
              
              <div style={styles.infoRow}>
                <span style={styles.label}>Looking for:</span>
                <span style={styles.value}>{userData.genderPreference}</span>
              </div>
              
              <div style={styles.infoRow}>
                <span style={styles.label}>Age Range:</span>
                <span style={styles.value}>
                  {userData.ageMin} - {userData.ageMax} years
                </span>
              </div>
              
              <div style={styles.infoRow}>
                <span style={styles.label}>Location:</span>
                <span style={styles.value}>
                  {userData.city}, {userData.state}
                </span>
              </div>
            </div>
            </>
          ) : (
            <p style={styles.noData}>No profile data found.</p>
          )}
        </div>

        <div style={styles.actionsCard}>
          <h3 style={styles.sectionTitle}>Next Steps</h3>
          <div style={styles.actionsList}>
            <div style={styles.actionItem}>
              <div style={styles.stepNumber}>1</div>
              <div style={styles.stepContent}>
                <div style={styles.stepTitle}>Verify Email</div>
                <div style={styles.stepDescription}>Check your inbox for verification link</div>
              </div>
            </div>
            <div style={styles.actionItem}>
              <div style={styles.stepNumber}>2</div>
              <div style={styles.stepContent}>
                <div style={styles.stepTitle}>Complete Profile</div>
                <div style={styles.stepDescription}>Add photos and preferences</div>
              </div>
            </div>
            <div style={styles.actionItem}>
              <div style={styles.stepNumber}>3</div>
              <div style={styles.stepContent}>
                <div style={styles.stepTitle}>Start Matching</div>
                <div style={styles.stepDescription}>We'll find compatible connections</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#fafafa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 40px',
    background: 'white',
    borderBottom: '1px solid #e5e7eb',
  },
  logo: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#C60000',
    margin: 0,
    letterSpacing: '-0.5px',
  },
  signOutBtn: {
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #e5e7eb',
    padding: '8px 20px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  welcomeCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '32px',
    marginBottom: '24px',
    border: '1px solid #e5e7eb',
  },
  welcomeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  welcomeTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  statusBadge: {
    background: '#f0fdf4',
    color: '#16a34a',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '500',
    border: '1px solid #bbf7d0',
  },
  welcomeMessage: {
    fontSize: '15px',
    lineHeight: '1.6',
    color: '#6b7280',
    marginBottom: '24px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
  infoItem: {
    padding: '16px',
    background: '#fafafa',
    borderRadius: '6px',
    border: '1px solid #f3f4f6',
  },
  infoLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  profileCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '32px',
    marginBottom: '24px',
    border: '1px solid #e5e7eb',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginTop: 0,
    marginBottom: '24px',
    letterSpacing: '-0.3px',
  },
  profilePictureContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  profilePicture: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '4px solid #f3f4f6',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  profileInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '16px',
    background: '#fafafa',
    borderRadius: '6px',
    border: '1px solid #f3f4f6',
  },
  label: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  value: {
    fontSize: '15px',
    color: '#1a1a1a',
    fontWeight: '500',
  },
  noData: {
    fontSize: '15px',
    color: '#9ca3af',
    textAlign: 'center',
    padding: '40px 0',
  },
  actionsCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '32px',
    border: '1px solid #e5e7eb',
  },
  actionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  actionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    background: '#fafafa',
    borderRadius: '6px',
    border: '1px solid #f3f4f6',
  },
  stepNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#C60000',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '4px',
  },
  stepDescription: {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.5',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#fafafa',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #f3f4f6',
    borderTop: '3px solid #C60000',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '15px',
    color: '#9ca3af',
    fontWeight: '500',
  },
};

export default Profile;
